// Copyright © 2019-2026 Focal Systems. All rights reserved.

import {
  type AssistantEvent,
  type AssistantMessage,
  type AssistantTurn,
  type FeedbackPair,
  MAX_HISTORY_TURNS,
  type Message,
  type PageContext,
} from "@/features/assistant/types";

export interface AssistantState {
  messages: Message[];
  isOpen: boolean;
  isExpanded: boolean;
  hasUnread: boolean;
  /** The viewer removed the page chip; cleared again by the next page change. */
  contextDismissed: boolean;
}

export type AssistantAction =
  | { type: "send"; userId: string; answerId: string; question: string; context: PageContext | null }
  | { type: "event"; answerId: string; event: AssistantEvent }
  | { type: "stopped"; answerId: string }
  | { type: "retry"; answerId: string }
  | { type: "reset" }
  | { type: "open" }
  | { type: "close" }
  | { type: "toggle" }
  | { type: "setExpanded"; value: boolean }
  | { type: "contextChanged"; id: string; context: PageContext | null }
  /** Same page, new label (a page publishes its title a render after its pathname changes). */
  | { type: "contextRelabeled"; context: PageContext }
  | { type: "dismissContext" }
  | { type: "feedback"; answerId: string; value: "up" | "down" }
  | { type: "feedbackSending"; answerId: string }
  | { type: "feedbackSent"; answerId: string; feedbackId: string }
  | { type: "feedbackFailed"; answerId: string }
  /** A failed send is dropped because the rating it carried was withdrawn. */
  | { type: "feedbackCleared"; answerId: string };

export function initialAssistantState(messages: Message[] = [], isExpanded = false): AssistantState {
  return { messages, isOpen: false, isExpanded, hasUnread: false, contextDismissed: false };
}

const LIVE = new Set(["working", "streaming"]);

/** A single leading slash: not `//host` or `/\\host`, which browsers treat as another origin. */
export function isInAppHref(href: string): boolean {
  return /^\/(?![/\\])/.test(href);
}

/** Knowledge Center pages are Notion pages; the bot only emits urls its own knowledge search returned. */
export const KNOWLEDGE_CENTER_PREFIX = "https://app.notion.com/p/";

/** The prefix plus URL-safe characters only: no whitespace, quotes, backslash or angle brackets. */
export function isKnowledgeCenterHref(href: string): boolean {
  return href.startsWith(KNOWLEDGE_CENTER_PREFIX) && /^[^\s\\"'<>]*$/.test(href.slice(KNOWLEDGE_CENTER_PREFIX.length));
}

export function isLinkHref(href: string): boolean {
  return isInAppHref(href) || isKnowledgeCenterHref(href);
}

function emptyAnswer(id: string, question: string, context: PageContext | null): AssistantMessage {
  return {
    id,
    role: "assistant",
    question,
    context,
    status: "working",
    steps: [],
    text: "",
    tables: [],
    links: [],
    notes: [],
    error: null,
    feedback: null,
    requestId: null,
    feedbackId: null,
    feedbackState: null,
  };
}

function updateAnswer(
  state: AssistantState,
  answerId: string,
  update: (answer: AssistantMessage) => AssistantMessage,
): AssistantState {
  return {
    ...state,
    messages: state.messages.map((m) => (m.id === answerId && m.role === "assistant" ? update(m) : m)),
  };
}

function applyEvent(answer: AssistantMessage, event: AssistantEvent): AssistantMessage {
  switch (event.type) {
    case "step": {
      const step = { id: event.id, label: event.label, status: event.status };
      const exists = answer.steps.some((s) => s.id === event.id);
      return {
        ...answer,
        steps: exists ? answer.steps.map((s) => (s.id === event.id ? step : s)) : [...answer.steps, step],
      };
    }
    case "text":
      return { ...answer, status: "streaming", text: answer.text + event.delta };
    case "table":
      return { ...answer, tables: [...answer.tables, event.table] };
    case "link":
      // Links come from the agent; only in-app paths and Knowledge Center pages reach the page.
      return isLinkHref(event.link.href) ? { ...answer, links: [...answer.links, event.link] } : answer;
    case "note":
      return { ...answer, notes: [...answer.notes, event.text] };
    case "requestId":
      return { ...answer, requestId: event.id };
    case "error":
      return { ...answer, status: "error", error: event.message };
    case "done":
      return { ...answer, status: "done", steps: answer.steps.map((s) => ({ ...s, status: "done" })) };
  }
}

export function assistantReducer(state: AssistantState, action: AssistantAction): AssistantState {
  switch (action.type) {
    case "send":
      return {
        ...state,
        messages: [
          ...state.messages,
          { id: action.userId, role: "user", text: action.question },
          emptyAnswer(action.answerId, action.question, action.context),
        ],
      };
    case "event": {
      const target = state.messages.find((m) => m.id === action.answerId);
      if (target?.role !== "assistant" || !LIVE.has(target.status)) return state;
      const next = updateAnswer(state, action.answerId, (a) => applyEvent(a, action.event));
      const finished = action.event.type === "done" || action.event.type === "error";
      return finished && !state.isOpen ? { ...next, hasUnread: true } : next;
    }
    case "stopped":
      return updateAnswer(state, action.answerId, (a) => (LIVE.has(a.status) ? { ...a, status: "stopped" } : a));
    case "retry":
      return updateAnswer(state, action.answerId, (a) => ({ ...emptyAnswer(a.id, a.question, a.context) }));
    case "reset":
      return { ...state, messages: [], hasUnread: false };
    case "open":
      return { ...state, isOpen: true, hasUnread: false };
    case "close":
      return { ...state, isOpen: false };
    case "toggle":
      return state.isOpen ? { ...state, isOpen: false } : { ...state, isOpen: true, hasUnread: false };
    case "setExpanded":
      return { ...state, isExpanded: action.value };
    case "contextChanged": {
      const base = { ...state, contextDismissed: false };
      if (!action.context || state.messages.length === 0) return base;
      const last = state.messages[state.messages.length - 1];
      const divider = {
        id: last?.role === "divider" ? last.id : action.id,
        role: "divider" as const,
        label: action.context.label,
      };
      const kept = last?.role === "divider" ? state.messages.slice(0, -1) : state.messages;
      return { ...base, messages: [...kept, divider] };
    }
    case "contextRelabeled": {
      const last = state.messages[state.messages.length - 1];
      if (last?.role !== "divider") return state;
      return { ...state, messages: [...state.messages.slice(0, -1), { ...last, label: action.context.label }] };
    }
    case "dismissContext":
      return { ...state, contextDismissed: true };
    case "feedback":
      return updateAnswer(state, action.answerId, (a) => ({
        ...a,
        feedback: a.feedback === action.value ? null : action.value,
      }));
    case "feedbackSending":
      return updateAnswer(state, action.answerId, (a) => ({ ...a, feedbackState: "sending" }));
    case "feedbackSent":
      return updateAnswer(state, action.answerId, (a) => ({
        ...a,
        feedbackId: action.feedbackId,
        feedbackState: "sent",
      }));
    case "feedbackFailed":
      return updateAnswer(state, action.answerId, (a) => ({ ...a, feedbackState: "failed" }));
    case "feedbackCleared":
      return updateAnswer(state, action.answerId, (a) => ({ ...a, feedbackState: null }));
  }
}

/** The single answer currently being produced, if any. */
export function liveAnswer(state: AssistantState): AssistantMessage | undefined {
  return state.messages.find((m): m is AssistantMessage => m.role === "assistant" && LIVE.has(m.status));
}

export function isBusy(state: AssistantState): boolean {
  return liveAnswer(state) !== undefined;
}

export function assistantStatus(state: AssistantState): "idle" | "working" | "streaming" | "error" {
  const live = liveAnswer(state);
  if (live) return live.status as "working" | "streaming";
  const lastAnswer = [...state.messages].reverse().find((m) => m.role === "assistant");
  return lastAnswer?.role === "assistant" && lastAnswer.status === "error" ? "error" : "idle";
}

/** Finished Q/A pairs before `answerId`, sent as follow-up context. */
export function historyBefore(messages: Message[], answerId: string): AssistantTurn[] {
  const end = messages.findIndex((m) => m.id === answerId);
  return messages
    .slice(0, end === -1 ? messages.length : end)
    .filter((m): m is AssistantMessage => m.role === "assistant" && m.status === "done")
    .map((m) => ({ question: m.question, answer: m.text }));
}

const FEEDBACK_STATUSES = new Set(["done", "stopped"]);

/** The rateable Q/A pairs up to and including `answerId` (the last 10), as posted to Slack. */
export function conversationFor(messages: Message[], answerId: string): FeedbackPair[] {
  const end = messages.findIndex((m) => m.id === answerId);
  return messages
    .slice(0, end === -1 ? messages.length : end + 1)
    .filter((m): m is AssistantMessage => m.role === "assistant" && FEEDBACK_STATUSES.has(m.status))
    .slice(-MAX_HISTORY_TURNS)
    .map((m) => ({ question: m.question, answer: shownAnswer(m), tables: m.tables, context: m.context }));
}

/** Everything the panel showed as the answer: a non-answer is often notes only, with no text at all. */
function shownAnswer(m: AssistantMessage): string {
  const links = m.links.map((l) => `${l.label} (${l.href})`);
  return [m.text, ...m.notes, ...links].filter((part) => part.trim()).join("\n\n");
}
